// Turning server timestamps into something readable, and nothing else.
//
// The client formats time. It never computes one: every `createdAt` here came from a
// server response, and there is no `new Date()` in this package outside of this file's
// "is that today" comparison, which reads the clock and writes nothing.
//
// Absolute, not relative. "3 minutes ago" is a number that keeps changing on a screen
// somebody is reading slowly, and in a conversation that may go quiet for a fortnight it
// turns waiting into a counter — which is the one thing the waiting design is against.

const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' });
const dayAndTimeFormat = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});
const day = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

export function dayAndTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return '';
  }
  const now = new Date();
  const sameDay =
    at.getFullYear() === now.getFullYear() &&
    at.getMonth() === now.getMonth() &&
    at.getDate() === now.getDate();
  return sameDay ? time.format(at) : dayAndTimeFormat.format(at);
}

export function dayOnly(iso: string): string {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? '' : day.format(at);
}

/**
 * A calendar date (`2026-08-27`) with no instant in it, as a release note carries.
 *
 * Not `dayOnly`: `new Date('2026-08-27')` is midnight UTC, which every timezone west of it
 * formats as the day before — a note dated the day before it shipped reads as a mistake.
 * A time with no zone on the end is parsed as local, which is what a bare date means here.
 */
export function calendarDay(date: string): string {
  const at = new Date(`${date}T00:00:00`);
  return Number.isNaN(at.getTime()) ? '' : day.format(at);
}

/** "Sally, David and Aunt Mary". The sentence the composer needs and nothing more. */
export function nameList(names: readonly string[]): string {
  if (names.length === 0) {
    return '';
  }
  if (names.length === 1) {
    return names[0] ?? '';
  }
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1] ?? ''}`;
}

/**
 * "Erica", "Seth & Erica", "Seth, Erica & 3 others" — the room switcher's own convention
 * (an ampersand, and a collapse to "& N others" past two names) rather than `nameList`'s
 * prose join. A sibling to `nameList` rather than a variant of it, per
 * `docs/archived/13-visual-refresh.md`: the two are shaped for different places (a sentence next
 * to the composer versus a compact hover label) and neither should grow a parameter that
 * makes it do the other's job.
 */
export function roomNameList(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? '';
  }
  if (names.length === 2) {
    return `${names[0]} & ${names[1]}`;
  }
  return `${names[0]}, ${names[1]} & ${names.length - 2} others`;
}

/**
 * The other principal's name, ONLY when this client can actually be sure who that is.
 *
 * This repo supports any number of principals, and the principals thread holds every one
 * of them (`docs/decisions.md`) — not just two. `Person` deliberately carries no role field at
 * all (`api/contract.ts`), because "users do not think in terms of principal and
 * interested party, and the interface does not present them". Put those two facts
 * together: the ONLY roster shape where "everyone but the reader" is guaranteed to mean
 * "the other principal(s)" is a plan with exactly two people, full stop — because then
 * there is nobody else it could be. The moment a plan has a third person, "everyone but
 * me" might be a second principal, or it might be an interested party who has joined, and
 * this client has no field to tell them apart.
 *
 * So this returns the name only for that one safe shape, and `null` for every other one —
 * callers are expected to fall back to a role-neutral phrase ("your co-principal(s)")
 * rather than name someone this client cannot actually verify is a principal at all. This
 * is a limitation of the current API contract, not a bug to quietly work around with a
 * better guess.
 */
export function namedCoPrincipal(
  people: readonly { readonly name: string; readonly self: boolean }[],
): string | null {
  if (people.length !== 2) {
    return null;
  }
  const other = people.find((person) => !person.self);
  return other?.name ?? null;
}

/** "Erica" from "Erica Hitchings" — everything before the first space, or the whole string
 * if there isn't one. Not a real name parser (a hyphenated given name, a name in a script
 * with no spaces, "Mary Jane" as one given name all split wrong) — this app tracks no
 * separate first and last name at all, and reciting someone's full name every time they're
 * mentioned reads worse than the rare wrong split. */
function firstName(name: string): string {
  const space = name.indexOf(' ');
  return space === -1 ? name : name.slice(0, space);
}

/** `namedCoPrincipal`'s first name when the plan is exactly two people, its role-neutral
 * fallback otherwise — the one phrase every caller that names the principals' room wants. */
export function principalsRoomLabel(
  people: readonly { readonly name: string; readonly self: boolean }[],
): string {
  const named = namedCoPrincipal(people);
  return named === null ? 'your co-principals' : firstName(named);
}
